import { useEffect, useId, useRef, type ReactNode } from 'react';
import { t } from '../i18n/index.ts';
import { Icon } from './Icon.tsx';

export function Eyebrow({ children, as: As = 'h2', id }: { children: ReactNode; as?: 'h2' | 'h3' | 'span' | 'p'; id?: string }) {
  return <As className="eyebrow" id={id}>{children}</As>;
}

export function SectionHeader({ title, id, action }: { title: string; id?: string; action?: ReactNode }) {
  return (
    <div className="section-header">
      <Eyebrow {...(id ? { id } : {})}>{title}</Eyebrow>
      {action}
    </div>
  );
}

/** Up/down arrow with tone: growth is mint, a fall is amber (never red). */
export function Delta({ value, children, className = '' }: { value: number; children: ReactNode; className?: string }) {
  const up = value >= 0;
  return (
    <span className={`delta ${up ? 'delta--up' : 'delta--down'} ${className}`}>
      <span aria-hidden="true">{up ? '↗' : '↘'}</span> {children}
    </span>
  );
}

export interface SegmentOption<T extends string | number> {
  readonly value: T;
  readonly label: string;
}

export function Segmented<T extends string | number>({
  label, options, value, onChange, variant = 'tabs', size = 'md',
}: {
  label: string;
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  variant?: 'tabs' | 'pill' | 'boxed';
  size?: 'sm' | 'md';
}) {
  return (
    <div role="group" aria-label={label} className={`segmented segmented--${variant} segmented--${size}`}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={o.value === value}
          className="segmented__option"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label, visibleLabel, disabled }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  visibleLabel?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="toggle-label">
      <input
        type="checkbox"
        role="switch"
        className="toggle"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        {...(visibleLabel ? {} : { 'aria-label': label })}
      />
      {visibleLabel}
    </label>
  );
}

export function ProgressBar({ percent, label }: { percent: number; label: string }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="progress" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamped)}>
      <div className="progress__fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}

export function Ring({ percent, tone = 'violet', size = 40 }: { percent: number; tone?: 'violet' | 'mint' | 'cyber' | 'terracotta'; size?: number }) {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--ghost-2)" strokeWidth="4" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`var(--${tone})`} strokeWidth="4" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - clamped / 100)} transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="ring__arc"
      />
      <text x={size / 2} y={size / 2 + 3.5} textAnchor="middle" className="ring__text">{Math.round(clamped)}%</text>
    </svg>
  );
}

export function Modal({ title, onClose, children, variant = 'dialog', footer, describedBy }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  variant?: 'dialog' | 'sheet';
  footer?: ReactNode;
  describedBy?: string;
}) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    // On touch, focusing a field would throw up the keyboard over the dialog, so focus the dialog itself.
    const touch = variant === 'sheet' || window.matchMedia('(pointer: coarse)').matches;
    const first = touch ? null : panel.current?.querySelector<HTMLElement>('input, select, textarea, button:not(.modal__close)');
    (first ?? panel.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
      if (e.key === 'Tab' && panel.current) {
        const focusables = [...panel.current.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), select, textarea')];
        const firstEl = focusables[0];
        const lastEl = focusables[focusables.length - 1];
        if (!firstEl || !lastEl) return;
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('has-modal');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('has-modal');
      previous?.focus?.();
    };
  }, [variant]);
  return (
    <div className={`modal modal--${variant}`}>
      <div className="modal__scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        className="modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        tabIndex={-1}
      >
        {variant === 'sheet' && <div className="modal__grabber" aria-hidden="true" />}
        <div className="modal__head">
          <h2 id={titleId} className="modal__title">{title}</h2>
          <button type="button" className="icon-button modal__close" aria-label={t('common.close')} onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, error, children, hint, htmlFor }: { label: string; error?: string | null; children: ReactNode; hint?: string; htmlFor: string }) {
  return (
    <div className={`field ${error ? 'field--error' : ''}`}>
      <label htmlFor={htmlFor} className="field__label">{label}</label>
      {children}
      {hint && !error && <span className="field__hint">{hint}</span>}
      {error && <span className="field__error" role="alert">{error}</span>}
    </div>
  );
}

const BRANDS: Readonly<Record<string, string>> = { chatgpt: 'ChatGPT' };

export const sourceName = (source: string): string => {
  const kind = source.startsWith('mcp:') ? source.slice(4) : source;
  if (kind === 'ios') return t('sync.source.ios');
  if (kind === 'web') return t('sync.source.web');
  return BRANDS[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1);
};

export function SourceBadge({ source }: { source: string }) {
  const kind = source.startsWith('mcp:') ? source.slice(4) : source;
  return <span className={`source-badge source-badge--${kind}`}>{sourceName(source)}</span>;
}
